import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as ROUTES from '../constants/routes';
import AnimatedPage from '../components/AnimatedPage';
import { toggleModal } from '../reducers/modalReducer';
import { setLoginType } from '../reducers/loginReducer';
import { setToken, postRequest, showMessage } from '../config';
import { trackFormSubmit, trackButtonClick, trackPopupOpen, trackPopupClose } from '../utils/gtm';

const IconMail = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,12 2,6" /></svg>;
const IconLock = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
const IconEye = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
const IconEyeOff = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>;

const Login = () => {

    const dispatch = useDispatch();
    const navigation = useNavigate();
    const modal = useSelector((state) => state.modal);
    const [searchParams, setSearchParams] = useSearchParams();
    const urlEmail = searchParams.get("email");
    const redirect = searchParams.get("redirect");
    const isBillingsearch = searchParams.get("billing");
    let [isLogin, setIsLogin] = useState(false);
    let [isLoading, setIsLoading] = useState(false);
    let [operation, setOperation] = useState('login');
    let [passwordType, setPasswordType] = useState(true);
    let [isValidNewPassword, setIsValidNewPassword] = useState(false);
    let [formData, setFormData] = useState({ email: '', password: '' });
    let [newPassword, setNewPassword] = useState({ password: '', confirmPassword: '' });
    let [passwordError, setPasswordError] = useState({ password: '', confirmPassword: '' });
    let [newPasswordType, setNewPasswordType] = useState({ passwordType: true, confirmPasswordType: true });
    let PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{}[\]|\\:;"'<>,.?/~`])[A-Za-z\d!@#$%^&*()\-_=+{}[\]|\\:;"'<>,.?/~`]+$/;

    const user = useSelector((state) => state.user);
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token && user && !location?.hash) {
            if (user && ['Member', 'User'].includes(user?.role)) {
                window.location.href = '/dashboard';
            } else if (user && user?.role == 'Advisor') {
                window.location.href = '/advisor-dashboard';
            } else if (user && user?.role == 'Investor') {
                window.location.href = '/investor-dashboard';
            }
        }
    }, [user]);

    useEffect(() => {
        let pathname = window.location.pathname;
        if (pathname.includes('login')) {
            let userAgent = navigator.userAgent || navigator.vendor || window.opera;
            let mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
            let isMobile = mobileRegex.test(userAgent);
            if (isMobile) dispatch(toggleModal({ open: 'mobileDevicesPopup' }));
        }
        let isManualAdd = localStorage.getItem('isManualAdd');
        if (isManualAdd) localStorage.removeItem('isManualAdd');
    }, []);

    useEffect(() => {
        if (urlEmail) setFormData((prevData) => ({ ...prevData, email: urlEmail }));
        window.scroll(0, 0);
        if (location.pathname.includes('reset_password')) {
            setOperation('setPassword');
        }
    }, []);

    useEffect(() => resetStates(), [operation]);
    useEffect(() => {
        if (urlEmail) setFormData((prevData) => ({ ...prevData, email: urlEmail }));

        if (formData['email'] !== '' && formData['password'] !== '') setIsLogin(true);
    }, [formData]);

    const handleOperation = (link) => {
        if (link == 'refresh') {
            trackButtonClick('login_refresh');
            location.pathname = '/login';
        } else {
            trackButtonClick(`login_${link}`);
            setOperation(link);
        }
    };

    const resetStates = () => {
        setIsLogin(false);
        setIsLoading(false);
        setPasswordType(true);
        setFormData({ email: '', password: '' });
        setNewPassword({ password: '', confirmPassword: '' });
        setNewPasswordType({ passwordType: true, confirmPasswordType: true });
    };

    const restrictedForDevices = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
        const isMobile = mobileRegex.test(userAgent)
        return isMobile
    }

    const login = async (e) => {
        if (restrictedForDevices()) {
            trackButtonClick('login_mobile_restricted');
            return showMessage('You can Login/Register on Laptop')
        }

        if (formData['email'] && formData['password']) {
            setIsLoading(true);
            const response = await postRequest('/user/auth', formData);
            if (response && response['code'] == 200) {
                if (response['body']['token']) {
                    showMessage(response['message']);
                    loginWithToken(response['body']);

                    dispatch(toggleModal({ open: '' }));
                    trackPopupClose('login');
                    trackFormSubmit('login_form', {
                        email: formData.email,
                        status: 'success',
                        user_type: response['body']['role']
                    });
                } else if (response['body']['hash']) {
                    let data = response['body'];
                    dispatch(setLoginType({ redirect: redirect, smsEnabled: data.smsEnabled, twoFaEnabled: data.twoFaEnabled, emailEnabled: data.emailEnabled, hash: data.hash, isBillingRoute: isBillingsearch }));
                    trackFormSubmit('login_form', {
                        email: formData.email,
                        status: 'requires_2fa',
                        auth_methods: {
                            sms: data.smsEnabled,
                            twoFa: data.twoFaEnabled,
                            email: data.emailEnabled
                        }
                    });
                }
                resetStates();
            } else {
                trackFormSubmit('login_form', {
                    email: formData.email,
                    status: 'failed',
                    error: response?.message
                });
                setIsLoading(false);
            }
        } else {
            trackButtonClick('login_validation_error');
            showMessage('Enter your email and password');
        }
    };

    const loginWithToken = async (data) => {
        for (const key of ['token'])    //role
            document.cookie = `${key}=${data[key]};path=/`;

        setToken(data['token']);
        localStorage.setItem('token', data['token']);
        localStorage.removeItem('anylistHowTo');
        if (data['role'] == 'Advisor') navigation(ROUTES['ADVISOR']);
        else if (data['role'] == 'Admin') setTimeout(() => navigation('/admin'), 100);
        else if (data['role'] == 'Investor') setTimeout(() => navigation(ROUTES['INVESTOR']), 100);
        else setTimeout(() => navigation(ROUTES['DASHBOARD']), 100);
    };

    const handlePasswordBlur = (type) => {
        let errors = { ...passwordError };
        const password = newPassword?.['password'];
        const confirmPassword = newPassword?.['confirmPassword'];

        if (type === 'password') {
            if (password === '' || password?.length < 8) errors['password'] = 'Password must be at least 8 characters long';
            else if (!PASSWORD_PATTERN.test(password)) errors['password'] = 'Password must contain atleast one capital letter, one special character and a number';
            else errors['password'] = '';
        }

        if (type === 'confirmPassword' || (password && confirmPassword)) {
            if (password === '' || password?.length < 8) errors['password'] = 'Password must be at least 8 characters long';
            else if (password !== confirmPassword) errors['confirmPassword'] = "Password and Confirm Password don't match";
            else if (!PASSWORD_PATTERN.test(password)) errors['password'] = 'Password must contain atleast one capital letter, one special character and a number';
            else { errors['confirmPassword'] = ''; }
        }

        setPasswordError(errors);
        if (!errors['password'] && !errors['confirmPassword']) setIsValidNewPassword(true);
    };

    const handleSetPassword = async () => {
        if (passwordError['password'] == '' && passwordError['confirmPassword'] == '') {
            if (!location.pathname.includes('reset_password')) {
                trackButtonClick('set_password_invalid_request');
                return showMessage('Invalid request try link from email');
            }
            setIsLoading(true);
            let params = location.pathname.split('/');
            let key = params[params.length - 1];

            trackFormSubmit('set_password_form', {
                status: 'attempting',
                has_errors: false
            });

            let response = await postRequest(`/user/setPassword`, { key, password: newPassword?.['password'] });
            if (response && response['code'] == 200) {
                resetStates();
                showMessage(response['message']);
                dispatch(toggleModal({ open: "passwordUpdatedSuccessfully" }));
                trackPopupOpen('passwordUpdatedSuccessfully');
                trackFormSubmit('set_password_form', {
                    status: 'success'
                });
            } else {
                trackFormSubmit('set_password_form', {
                    status: 'failed',
                    error: response?.message
                });
            }
            setIsLoading(false);
        } else {
            trackFormSubmit('set_password_form', {
                status: 'validation_error',
                errors: passwordError
            });
        }
    };


    return (
        <AnimatedPage>
            <div className="lp-page">
                <div className="lp-auth-main">
                    <div className="lp-auth-glow lp-auth-glow--tr" aria-hidden="true" />
                    <div className="lp-auth-glow lp-auth-glow--bl" aria-hidden="true" />

                    <div className="lp-auth-wrap lp-auth-wrap--narrow">
                        <div className="lp-auth-card">
                            {operation == 'login' ? (
                                <>
                                    <h1 className="lp-auth-title">Login to your</h1>
                                    <h1 className="lp-auth-title pink">Diligentsia account</h1>

                                    <div className="lp-auth-field lp-auth-field--icon-wide">
                                        <span className="icon"><IconMail /></span>
                                        <input
                                            id='email'
                                            type='email'
                                            value={formData?.['email'] || urlEmail || ''}
                                            placeholder='Email address'
                                            onChange={(e) => {
                                                if (restrictedForDevices()) { return showMessage('You can Login/Register on Laptop') }
                                                setFormData({ ...formData, email: e['target']['value'] })
                                            }}
                                        />
                                    </div>

                                    <div className="lp-auth-field lp-auth-field--icon-wide lp-auth-field--pw">
                                        <span className="icon"><IconLock /></span>
                                        <input
                                            id='password'
                                            type={passwordType ? 'password' : 'text'}
                                            placeholder='Password'
                                            value={formData?.['password'] || ''}
                                            onChange={(e) => {
                                                if (restrictedForDevices()) {
                                                    return showMessage('You can Login/Register on Laptop')
                                                }
                                                setFormData({ ...formData, password: e['target']['value'] })
                                            }}
                                        />
                                        <button type="button" className="toggle-pw" onClick={() => setPasswordType(!passwordType)}>
                                            {passwordType ? <IconEye /> : <IconEyeOff />}
                                        </button>
                                    </div>

                                    <div className="lp-auth-actions mt2rem">
                                        <button
                                            disabled={!isLogin}
                                            className={`lp-auth-submit ${!isLogin ? 'is-disabled' : ''}`}
                                            onClick={() => login()}
                                        >
                                            {isLoading ? <i className="fa fa-spinner fa-spin"></i> : 'Login'}
                                        </button>
                                    </div>

                                    <div className="lp-auth-links">
                                        <Link className="pink" to={ROUTES.REGISTER} onClick={() => trackButtonClick('create_account_link')}>
                                            Create new account
                                        </Link>
                                        <Link className="blue" to={ROUTES.FORGOT_PASSWORD} onClick={() => trackButtonClick('forgot_password_link')}>
                                            Forgotten password?
                                        </Link>
                                    </div>
                                </>
                            ) : operation == 'setPassword' ? (
                                <>
                                    <h1 className="lp-auth-title">Set New</h1>
                                    <h1 className="lp-auth-title pink">Password</h1>

                                    <div>
                                        <div className="lp-auth-field lp-auth-field--icon-wide lp-auth-field--pw">
                                            <span className="icon"><IconLock /></span>
                                            <input
                                                id='newPassword'
                                                type={newPasswordType['passwordType'] ? 'password' : 'text'}
                                                placeholder='New Password'
                                                value={newPassword?.['password'] || ''}
                                                onChange={(e) => { setNewPassword({ ...newPassword, password: e['target']['value'] }); setIsValidNewPassword(false); setPasswordError({ password: '', confirmPassword: '' }) }}
                                                onBlur={(e) => handlePasswordBlur('password')}
                                                required
                                            />
                                            <button type="button" className="toggle-pw" onClick={() => setNewPasswordType({ ...newPasswordType, passwordType: !newPasswordType['passwordType'] })}>
                                                {newPasswordType['passwordType'] ? <IconEye /> : <IconEyeOff />}
                                            </button>
                                        </div>
                                        {passwordError['password'] && <div className="lp-auth-error">{passwordError['password']}</div>}
                                    </div>

                                    <div>
                                        <div className="lp-auth-field lp-auth-field--icon-wide lp-auth-field--pw">
                                            <span className="icon"><IconLock /></span>
                                            <input
                                                id='confirmNewPassword'
                                                type={newPasswordType['confirmPasswordType'] ? 'password' : 'text'}
                                                placeholder='Confirm New Password'
                                                value={newPassword?.['confirmPassword'] || ''}
                                                onChange={(e) => { setNewPassword({ ...newPassword, confirmPassword: e['target']['value'] }); setIsValidNewPassword(false); setPasswordError({ password: '', confirmPassword: '' }) }}
                                                onBlur={(e) => handlePasswordBlur('confirmPassword')}
                                                required
                                            />
                                            <button type="button" className="toggle-pw" onClick={() => setNewPasswordType({ ...newPasswordType, confirmPasswordType: !newPasswordType['confirmPasswordType'] })}>
                                                {newPasswordType['confirmPasswordType'] ? <IconEye /> : <IconEyeOff />}
                                            </button>
                                        </div>
                                        {passwordError['confirmPassword'] && <div className="lp-auth-error">{passwordError['confirmPassword']}</div>}
                                    </div>

                                    <div className="lp-auth-actions">
                                        <button
                                            className={`lp-auth-submit ${!isValidNewPassword ? 'is-disabled' : ''}`}
                                            onClick={() => handleSetPassword()}
                                        >
                                            {isLoading ? <i className="fa fa-spinner fa-spin"></i> : 'Set Password'}
                                        </button>
                                    </div>

                                    <div className="lp-auth-links">
                                        <Link className="pink" to={ROUTES.REGISTER} onClick={() => trackButtonClick('create_account_link')}>
                                            Create new account
                                        </Link>
                                        <span className="blue" style={{ cursor: 'pointer' }} onClick={() => handleOperation('refresh')}>
                                            Login
                                        </span>
                                    </div>
                                </>
                            ) : null}
                        </div>

                        <p className="lp-auth-trust">Trusted by 50+ investors, advisors &amp; companies</p>
                    </div>
                </div>
            </div>
        </AnimatedPage>
    );

}

export default Login;
